/**
 * **La sfida a un amico su un gioco della piattaforma** — la parte pura: quali giochi si offrono
 * nel pannello (I-207, Luca 2026-09-26: «gli amici si devono poter invitare a sfide anche in altri
 * giochi (come per esempio backgammon)»).
 *
 * La sfida vera e' il secondo lotto di ADR-0163 §9: un torneo da 2 in qTournaments, senza Elo e
 * senza premio (L-2160, L-2172). Esiste dal contratto PR169 (`POST /v1/challenges`, kb#296/kb#297):
 * il pannello fa scegliere il gioco e la modalita', e la manda (`useInvioSfidaPiattaforma`).
 *
 * ✅ **SI OFFRONO SOLO I GIOCHI 1v1 (L-2213: «i giochi di cui possiamo creare match 1vs1»).**
 * Qui fino al 27/09 c'era un ripiego dichiarato, tutti i giochi del catalogo, perche' `/v1/games` non
 * lo diceva. Da limulo#152 (`bd61d4d`, kb#297 domanda 1) la GameTitle porta `supportsOneVsOne`, e il
 * filtro sta qui e in nessun altro posto, come promesso. Un campo ASSENTE vale `false`: e' il difetto
 * del BFF, e un BFF di prima di #152 non offre nessun gioco invece di offrirli tutti.
 * ⚠️ **Nessun gioco passa per un'altra porta** (lotto C1, T2-3): fino a C1 i giochi del ponte
 * (il backgammon dell'invito nativo, L-2288) entravano anche a `supportsOneVsOne` spento. Il
 * ponte lato invio e' smontato: il backgammon si offre come gli altri, SOLO se il BFF lo dichiara
 * 1v1 (su staging lo dichiara, kb#297). Uno staging che torna a un BFF di prima di #152 non offre
 * piu' il backgammon: e' il difetto del BFF, e si vede.
 * [R @phomea, kb#297 06:49Z] Su staging lo portano acceso backgammon (100), Clash Royale (1) ed eFootball.
 * Il catalogo e' gia' dei soli giochi ATTIVI: `game.list` chiede `activeOnly: true` per difetto
 * (`qlash-limulo` `app-games/backend/capability-resolvers.js:26`), quindi qui non si filtra lo `status`.
 */
import type { LimuloGame, LimuloGameMatchMode } from '../../types/limulo.ts';

import { limuloFailure, type LimuloFailure } from './errors.ts';
import { labelOf } from './profiles.ts';

/** Un gioco offerto nel pannello: il codice che il torneo portera' e il nome da mostrare. */
export interface GiocoDellaSfida {
  /**
   * Il CODICE del gioco (`LimuloGame.gameId`, kb#81). ⚠️ Il BFF lo riempie con l'ObjectId quando il
   * gioco non ha un codice (`sanitizePublicGame`: `game.gameId || game.id || ''`): qui arriva cio'
   * che il catalogo dice, e chi lo mandera' al motore lo sa.
   */
  gameId: string;
  nome: string;
}

export type CatalogoDellaSfida =
  /** Il catalogo non ha ancora risposto (o sta rileggendo dopo un errore). */
  | { kind: 'caricamento' }
  /** Il catalogo ha risposto con un errore, e non c'e' un elenco da mostrare. */
  | { kind: 'errore'; failure: LimuloFailure }
  /** Il catalogo c'e'. `giochi` puo' essere vuoto, e allora il pannello lo dice. */
  | { kind: 'ok'; giochi: GiocoDellaSfida[] };

/**
 * Lo stato del catalogo, dalla query di `/v1/games`.
 *
 * - l'ordine e' quello del server: chi ordina il catalogo non sta nella TMA;
 * - un gioco che non dichiara `supportsOneVsOne: true` si scarta (L-2213, kb#297), senza eccezioni;
 * - un gioco senza codice si scarta, perche' non si potrebbe sfidare su niente;
 * - un codice ripetuto si prende una volta sola;
 * - ⚠️ un elenco gia' letto **vince** su un errore di una rilettura successiva: RTK tiene `data`
 *   accanto a `isError`, e un catalogo che cambia raramente non si butta per un giro fallito.
 */
export function catalogoDellaSfida(query: {
  data?: readonly LimuloGame[];
  isError: boolean;
  error?: unknown;
}): CatalogoDellaSfida {
  if (query.data === undefined) {
    return query.isError ? { kind: 'errore', failure: limuloFailure(query.error) } : { kind: 'caricamento' };
  }
  const visti = new Set<string>();
  const giochi: GiocoDellaSfida[] = [];
  for (const g of query.data) {
    if (g.supportsOneVsOne !== true || g.gameId === '' || visti.has(g.gameId)) continue;
    visti.add(g.gameId);
    giochi.push({ gameId: g.gameId, nome: labelOf(g, g.gameId) });
  }
  return { kind: 'ok', giochi };
}

/**
 * La modalita' che la sfida a backgammon preseleziona: 3 punti, rapid — i 3 punti che il ponte
 * preselezionava (decisione del seggio d'accordo con qgammon-orcr, 2026-09-29 13:25Z).
 * [C `qlash-limulo@origin/staging` `9f077b8`, `app-qgammon/backend/provider.js`] la `key` del
 * provider e' `backgammon-<punti>pt-<controllo>` (`matchModeKey`). Una preselezione, mai un
 * obbligo: se il BFF non serve quella `key` fra le modalita' inviabili non si sceglie niente, e
 * l'utente sceglie a mano.
 */
export const BACKGAMMON_MODALITA_PREDEFINITA = 'backgammon-3pt-rapid';

/** Una modalita' del gioco scelto, come la mostra il pannello. */
export interface ModalitaDelGioco {
  /** La `key` del provider (per il backgammon `backgammon-3pt-rapid` e simili): e' per-gioco. */
  key: string;
  nome: string;
}

export type ModalitaDellaSfida =
  | { kind: 'caricamento' }
  /**
   * Le modalita' non si leggono. Il pannello non afferma «nessuna modalita'». Sull'errore la
   * lista in sola lettura non offre «Riprova» (l'errore piu' probabile e' un'utenza senza
   * identita' mobile, 500, kb#297, che non passa); dalla lobby backgammon, dove le modalita'
   * decidono se il gioco si puo' sfidare, «Riprova» si offre sui SOLI errori che riprovando
   * possono passare (`catalogoRiprovabile`).
   */
  | { kind: 'errore' }
  | { kind: 'ok'; modalita: ModalitaDelGioco[] };

/**
 * Le modalita' del gioco scelto, da `GET /v1/games/:gameId/match-modes` (kb#297 domanda 2).
 * Sono le modalita' fra cui il pannello fa scegliere (la lista in sola lettura, `ModalitaVista`,
 * resta per chi monta il pannello senza `invio`); l'invio le filtra ancora per `teamSize`
 * (`modaliteInviabili`).
 * - l'ordine e' quello del server;
 * - una `key` vuota o ripetuta si scarta;
 * - senza `label` si mostra la `key`;
 * - un elenco gia' letto vince sull'errore di una rilettura, come per il catalogo.
 * ⚠️ `data` deve essere `currentData` di RTK, **mai** `data` (revisione di #463, 1): cambiando gioco,
 * `data` tiene le modalita' del gioco di PRIMA (`lastResult`), e se la lettura nuova fallisce le tiene
 * per sempre. E' la trappola gia' scritta in `routes/limulo/LimuloMatchDetail.tsx`.
 */
export function modalitaDellaSfida(query: {
  data?: readonly LimuloGameMatchMode[];
  isError: boolean;
}): ModalitaDellaSfida {
  if (query.data === undefined) return query.isError ? { kind: 'errore' } : { kind: 'caricamento' };
  const viste = new Set<string>();
  const modalita: ModalitaDelGioco[] = [];
  for (const m of query.data) {
    if (m.key === '' || viste.has(m.key)) continue;
    viste.add(m.key);
    modalita.push({ key: m.key, nome: m.label !== '' ? m.label : m.key });
  }
  return { kind: 'ok', modalita };
}

/**
 * Riprovare serve solo quando il guasto puo' passare da solo: la rete, la nostra scadenza, un 5xx,
 * un 429 (troppe richieste: passa da solo, come dice anche `lib/qsocials/realtime.ts`).
 * Su un rifiuto dichiarato (interruttore, permessi, app spenta) «Riprova» sarebbe una promessa
 * falsa: e' la regola dei verdetti di `gating.ts`.
 */
export function catalogoRiprovabile(failure: LimuloFailure): boolean {
  return (
    failure.verdict === 'network' ||
    failure.verdict === 'timeout' ||
    failure.status === 429 ||
    (failure.status !== null && failure.status >= 500)
  );
}
